// src/products/dataroom/pages/BookmarksPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Bookmark as BookmarkIcon, FileText, Folder, Trash2, Loader2, ChevronRight } from 'lucide-react';
import dataroomService from '../../../services/dataroomService';
import { useNavigate } from 'react-router-dom';

const BookmarksPage = ({ user, projectId, selectedProject, onMessage }) => {
    const navigate = useNavigate();
    const [bookmarks, setBookmarks] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchBookmarks = useCallback(async () => {
        if (!user?.id) {
            setLoading(false);
            return;
        }

        // If no projectId, fetch all bookmarks for all projects
        setLoading(true);
        try {
            if (projectId) {
                // Fetch for specific project
                const data = await dataroomService.fetchBookmarks(user.id, projectId);
                setBookmarks(data || []);
            } else {
                // Fetch all bookmarks across all projects
                const data = await dataroomService.fetchAllBookmarks(user.id);
                setBookmarks(data || []);
            }
        } catch (error) {
            console.error('Error fetching bookmarks:', error);
            onMessage && onMessage(`Failed to load bookmarks: ${error.message}`, 'error');
            setBookmarks([]);
        } finally {
            setLoading(false);
        }
    }, [user?.id, projectId, onMessage]);

    useEffect(() => {
        fetchBookmarks();
    }, [fetchBookmarks]);

    const handleDelete = async (bookmarkId) => {
        if (!window.confirm("Are you sure you want to remove this bookmark?")) return;

        try {
            await dataroomService.deleteBookmark(bookmarkId);
            onMessage("Bookmark removed successfully.", 'success');
            fetchBookmarks(); // Refresh the list
        } catch (error) {
            onMessage(`Failed to remove bookmark: ${error.message}`, 'error');
        }
    };

    // Allow viewing bookmarks without a specific project selected (shows all bookmarks)

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                    <BookmarkIcon className="h-7 w-7 text-blue-600" />
                    <h1 className="text-2xl font-bold text-gray-900">Bookmarks</h1>
                </div>
                {bookmarks.length > 0 && (
                    <span className="text-sm text-gray-500">{bookmarks.length} bookmark{bookmarks.length !== 1 ? 's' : ''}</span>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
            ) : bookmarks.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <BookmarkIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Bookmarks Found</h3>
                    <p className="text-gray-600">Bookmark files or folders from the File Explorer to see them here.</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Location
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Note
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {bookmarks.map(bookmark => {
                                const item = bookmark.dataroom_items;
                                const project = bookmark.dataroom_projects;
                                const isFolder = item?.is_folder || false;
                                const ItemIcon = isFolder ? Folder : FileText;
                                
                                return (
                                    <tr key={bookmark.id} className="hover:bg-gray-50 transition-colors">
                                        {/* Location Column */}
                                        <td className="px-4 py-2">
                                            <div className="flex items-center space-x-2 text-sm">
                                                <ItemIcon className={`h-4 w-4 flex-shrink-0 ${isFolder ? 'text-blue-600' : 'text-gray-600'}`} />
                                                <div className="flex items-center min-w-0">
                                                    <span className="text-gray-700 font-medium truncate">{project?.project_name || 'Unknown Project'}</span>
                                                    <ChevronRight className="h-3 w-3 text-gray-400 mx-1 flex-shrink-0" />
                                                    <span className="text-gray-900 font-semibold truncate">{bookmark.name || item?.name || 'Unnamed'}</span>
                                                    {isFolder && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-50 text-blue-700 rounded flex-shrink-0">Folder</span>}
                                                </div>
                                            </div>
                                        </td>

                                        {/* Note Column */}
                                        <td className="px-4 py-2">
                                            {bookmark.notes ? (
                                                <p className="text-sm text-gray-600 truncate max-w-md" title={bookmark.notes}>
                                                    {bookmark.notes}
                                                </p>
                                            ) : (
                                                <span className="text-xs text-gray-400 italic">No note</span>
                                            )}
                                        </td>

                                        {/* Actions Column */}
                                        <td className="px-4 py-2 text-right">
                                            <div className="flex items-center justify-end space-x-2">
                                                <button
                                                    onClick={() => navigate(`/dataroom/${bookmark.project_id}`)}
                                                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                                                    title="Go to this item"
                                                >
                                                    Open
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(bookmark.id)}
                                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                    title="Remove bookmark"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default BookmarksPage;